import {
  ArrowLeftOutlined,
  CheckOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { CustomInput } from "components/formik";
import { ALL_IN_ONE_PLAN, PAYMENT_PLAN, PAYMENT_PROVIDERS } from "constants/plans";
import { HOME } from "constants/routes";
import { Field, useFormikContext, FieldArray } from "formik";
import PropType from "prop-types";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { SocialIcon } from "react-social-icons";
import { normalizeProfileLink } from "utils/profileLinks";
import { v4 as uuidv4 } from "uuid";

const ProfileActions = ({ history, isLoading, submitForm }) => (
  <div className="edit-user-action">
    <button
      className="button button-muted w-100-mobile"
      disabled={isLoading}
      onClick={() => history.push(HOME)}
      type="button"
    >
      <ArrowLeftOutlined />
      &nbsp; Back
    </button>
    <button
      className="button w-100-mobile"
      disabled={isLoading}
      onClick={submitForm}
      type="button"
    >
      {isLoading ? <LoadingOutlined /> : <CheckOutlined />}
      &nbsp;
      {isLoading ? "Updating Profile" : "Update Profile"}
    </button>
  </div>
);

const EditForm = ({ isLoading, authProvider, plan }) => {
  const history = useHistory();
  const { values, submitForm, setFieldValue } = useFormikContext();
  const [isChangePass, setChangePass] = useState(false);
  const isAllInOne = plan === ALL_IN_ONE_PLAN;

  const onLinkIconChange = (event, index) => {
    const file = event.target.files && event.target.files[0];

    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      window.alert("Custom link icons must be JPEG or PNG files.");
      event.target.value = "";
      return;
    }

    if (file.size > 1024 * 1024) {
      window.alert("Custom link icons must be 1 MB or smaller.");
      event.target.value = "";
      return;
    }

    setFieldValue(`friends.${index}.iconFile`, file);
    setFieldValue(`friends.${index}.iconPreview`, URL.createObjectURL(file));
    event.target.value = "";
  };

  if (plan === PAYMENT_PLAN) {
    return (
      <div className="user-profile-details">
        <Field
          component={CustomInput}
          disabled={isLoading}
          label="* Full Name"
          name="fullname"
          placeholder="Enter your full name"
          style={{ textTransform: "capitalize" }}
          type="text"
        />
        <h4 className="text-center">Payment Links</h4>
        <p className="text-center text-subtle">
          Add one or more links for each supported payment provider.
        </p>
        <FieldArray name="paymentLinks">
          {({ push, remove }) => (
            <div>
              {PAYMENT_PROVIDERS.map((provider) => {
                const providerLinks = values.paymentLinks
                  .map((link, index) => ({ ...link, index }))
                  .filter((link) => link.provider === provider.value);

                return (
                  <div className="payment-dashboard-group" key={provider.value}>
                    <div className="payment-dashboard-provider">
                      <img alt={`${provider.label} logo`} src={provider.logo} />
                      <h5>{provider.label}</h5>
                    </div>
                    {providerLinks.map((link, providerIndex) => (
                      <div className="payment-dashboard-input" key={`${provider.value}-${link.index}`}>
                        <Field
                          component={CustomInput}
                          disabled={isLoading}
                          label={`${provider.label} link${providerIndex > 0 ? ` #${providerIndex + 1}` : ""}`}
                          name={`paymentLinks.${link.index}.url`}
                          placeholder="https://..."
                          type="text"
                        />
                        <Field
                          component={CustomInput}
                          disabled={isLoading}
                          label="Text shown under the payment icon"
                          name={`paymentLinks.${link.index}.label`}
                          placeholder="Phone number, branch name, or account name"
                          type="text"
                        />
                        {providerLinks.length > 1 ? (
                          <button
                            className="button button-small button-muted"
                            onClick={() => remove(link.index)}
                            type="button"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    ))}
                    <button
                      className="button button-small"
                      onClick={() => push({ provider: provider.value, url: "", label: "" })}
                      type="button"
                    >
                      Duplicate {provider.label}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </FieldArray>
        <ProfileActions history={history} isLoading={isLoading} submitForm={submitForm} />
      </div>
    );
  }

  return (
    <div className="user-profile-details">
      <Field
        disabled={isLoading}
        name="fullname"
        type="text"
        label="* Full Name"
        placeholder="Enter your full name"
        component={CustomInput}
        style={{ textTransform: "capitalize" }}
      />

      <Field
        disabled={authProvider !== "password" || isLoading}
        name="email"
        type="email"
        label="* Email Address"
        placeholder="test@example.com"
        component={CustomInput}
      />
      {!isChangePass && authProvider === "password" ? (
        <button
          type="button"
          className="button button-small"
          onClick={() => setChangePass(true)}
        >
          Change Password
        </button>
      ) : null}

      {isChangePass ? (
        <Field
          disabled={authProvider !== "password" || isLoading}
          name="newPassword"
          type="password"
          label="New Password"
          placeholder=""
          component={CustomInput}
        />
      ) : null}

      <Field
        disabled={isLoading}
        name="job"
        type="text"
        label="* Job Title"
        placeholder="Accounting Manager"
        component={CustomInput}
      />

      <Field
        disabled={isLoading}
        name="website"
        type="text"
        label="Website"
        placeholder="https://www.YourWebsite.com"
        component={CustomInput}
      />
      <Field
        disabled={isLoading}
        name="bio"
        type="text"
        label="BIO"
        placeholder="All your dreams can come true and I'll make sure of it ⚡️🌎."
        component={CustomInput}
      />

      <Field
        disabled={isLoading}
        name="address"
        type="text"
        label="Address"
        placeholder="3rd District, 6 Oct, Giza, Egypt"
        component={CustomInput}
        style={{ textTransform: "capitalize" }}
      />
      <Field
        disabled={isLoading}
        name="gMaps"
        type="text"
        label="Location URL (Google Maps)"
        placeholder="https://goo.gl/maps/@Your_Google_Coordiantes"
        component={CustomInput}
      />

      {/*START HERE */}

      <FieldArray name="friends">
        {({ remove, push }) => (
          <div>
            <h5 className="text-subtle pb-3">My Links</h5>
            <p className="text-subtle">
              {isAllInOne
                ? "Add social accounts or any custom link, with an optional custom logo."
                : "Add social-media profile links only. Custom links and logos require All in One."}
            </p>

            <button
              type="button"
              className="button button-small"
              onClick={() => push({ id: uuidv4(), name: "", label: "", icon: "" })}
            >
              {isAllInOne ? "Add New Link" : "Add Social Account"}
            </button>
            <br />
            {values.friends.length > 0 &&
              values.friends.map((friend, index) => (
                <div key={friend.id || index}>
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "5px",
                      borderRadius: "25px",
                    }}
                  >
                    {/* <label htmlFor={`friends.${index}.name`}>Name</label> */}
                    <div className="d-flex-center">
                      {isAllInOne && (friend.iconPreview || friend.icon) ? (
                        <img
                          alt="Custom link icon preview"
                          src={friend.iconPreview || friend.icon}
                          style={{ borderRadius: "50%", height: 50, objectFit: "cover", width: 50 }}
                        />
                      ) : (
                        <SocialIcon
                          url={normalizeProfileLink(friend.name) || "https://link"}
                          target="_blank"
                        />
                      )}
                    </div>
                    <Field
                      label={`Link # ${index + 1}`}
                      disabled={isLoading}
                      name={`friends.${index}.name`}
                      placeholder="https://www.socialprovider.com/Your_Profile_Name"
                      type="text"
                      component={CustomInput}
                    />
                    <Field
                      label="Text shown under the icon"
                      disabled={isLoading}
                      name={`friends.${index}.label`}
                      placeholder="For example: Follow me"
                      type="text"
                      component={CustomInput}
                    />
                    {isAllInOne ? <div style={{ marginBottom: 15 }}>
                      <span className="text-subtle" style={{ display: "block", marginBottom: 8 }}>
                        Custom logo for a non-social link (optional)
                      </span>
                      <label
                        className="button button-small"
                        htmlFor={`link-icon-${friend.id || index}`}
                      >
                        {friend.iconPreview || friend.icon ? "Replace Logo" : "Upload Logo"}
                      </label>
                      <input
                        accept="image/jpeg,image/png"
                        disabled={isLoading}
                        hidden
                        id={`link-icon-${friend.id || index}`}
                        onChange={(event) => onLinkIconChange(event, index)}
                        type="file"
                      />
                      {friend.iconPreview || friend.icon ? (
                        <button
                          className="button button-small button-muted"
                          onClick={() => {
                            setFieldValue(`friends.${index}.icon`, "");
                            setFieldValue(`friends.${index}.iconFile`, null);
                            setFieldValue(`friends.${index}.iconPreview`, "");
                          }}
                          style={{ marginLeft: 8 }}
                          type="button"
                        >
                          Remove Logo
                        </button>
                      ) : null}
                    </div> : null}
                    <br />
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() => remove(index)}
                    >
                      Delete
                    </button>
                    {/* <label htmlFor={`friends.${index}.email`}>Email</label> */}
                  </div>
                  <br />
                </div>
              ))}
          </div>
        )}
      </FieldArray>
      {/*START HERE */}

      <Field
        disabled={isLoading}
        name="whatsapp"
        type="text"
        label="WhatsApp Number or Link"
        placeholder="201234567890 or https://wa.me/message/..."
        component={CustomInput}
      />

      <Field
        disabled={isLoading}
        name="whatsappLabel"
        type="text"
        label="Text shown under the WhatsApp icon"
        placeholder="For example: Chat with me"
        component={CustomInput}
      />

      <Field
        disabled={isLoading}
        name="mobile"
        type="text"
        label="Personal Number"
        placeholder="971543090859"
        component={CustomInput}
      />

      <Field
        disabled={isLoading}
        name="bisMobile"
        type="text"
        label="Business Number"
        placeholder="971543090859"
        component={CustomInput}
      />

      {/* <input
                        accept="image/x-png,image/jpeg"
                        disabled={isLoading}
                        hidden
                        id="edit-avatar"
                        onChange={(e) => onFileChange(e, { name: 'avatar', type: 'single' })}
                        type="file"
                      /> */}
      {/* <CustomMobileInput
        defaultValue={values.mobile}
        name="mobile"
        disabled={isLoading}
        label="Personal Mobile Number (Will be used for checkout)"
      />

      <br />
      <CustomMobileInput
        defaultValue={values.bisMobile}
        name="bisMobile"
        disabled={isLoading}
        label="Business Mobile Number"
      /> */}

      <br />
      <ProfileActions history={history} isLoading={isLoading} submitForm={submitForm} />
    </div>
  );
};

EditForm.propTypes = {
  isLoading: PropType.bool.isRequired,
  authProvider: PropType.string.isRequired,
  plan: PropType.string.isRequired,
};

ProfileActions.propTypes = {
  history: PropType.shape({ push: PropType.func }).isRequired,
  isLoading: PropType.bool.isRequired,
  submitForm: PropType.func.isRequired,
};

export default EditForm;
